import Supercluster from "supercluster";

import { HEX_OVERLAY_CATEGORIES, HEX_OVERLAY_STYLE, HEX_OVERLAY_SUPERCLUSTER_OPTIONS, assertOverlayCategory } from "./overlay-config.js";
import { validateMockOverlayReport } from "./mock-overlay-reports.js";

export const OVERLAY_CLUSTER_BBOX_WORLD = Object.freeze([-180, -85, 180, 85]);

export function buildOverlayClusterIndex(category, reports, options = {}) {
  assertOverlayCategory(category);
  if (!Array.isArray(reports)) throw new TypeError(`Expected an array of ${category} overlay reports.`);
  const config = HEX_OVERLAY_CATEGORIES[category];
  const pointFeatures = reports.map((report, index) => overlayReportToFeature(report, category, index));
  const index = new Supercluster({
    radius: options.radius ?? HEX_OVERLAY_SUPERCLUSTER_OPTIONS.radius,
    maxZoom: options.maxZoom ?? HEX_OVERLAY_SUPERCLUSTER_OPTIONS.maxZoom,
    minPoints: options.minPoints ?? HEX_OVERLAY_SUPERCLUSTER_OPTIONS.minPoints,
  }).load(pointFeatures);

  return Object.freeze({
    category,
    config,
    index,
    pointFeatures: Object.freeze(pointFeatures),
  });
}

export function buildOverlayClusterIndexes(reportsByCategory, options = {}) {
  if (!reportsByCategory || typeof reportsByCategory !== "object") throw new TypeError("Expected overlay reports grouped by category.");
  return Object.freeze(Object.fromEntries(
    Object.keys(HEX_OVERLAY_CATEGORIES).map((category) => [
      category,
      buildOverlayClusterIndex(category, reportsByCategory[category] ?? [], options),
    ]),
  ));
}

export function renderOverlayClusters(categoryIndex, zoom, { bbox = OVERLAY_CLUSTER_BBOX_WORLD } = {}) {
  if (!categoryIndex?.index || !categoryIndex?.config) throw new TypeError("Expected an overlay Supercluster index.");
  const queryZoom = flooredOverlayZoom(zoom);
  const features = categoryIndex.index.getClusters(bbox, queryZoom).map((feature) => {
    if (feature.properties?.cluster) return toOverlayClusterFeature(feature, categoryIndex);
    return toOverlayPointFeature(feature, categoryIndex);
  });
  return Object.freeze({
    zoom: queryZoom,
    type: "FeatureCollection",
    features: Object.freeze(features),
  });
}

export function getOverlayClusterExpansionZoom(categoryIndex, clusterId) {
  if (!categoryIndex?.index) throw new TypeError("Expected an overlay Supercluster index.");
  const numericId = Number(clusterId);
  if (!Number.isInteger(numericId)) throw new TypeError("Expected an integer Supercluster cluster ID.");
  return categoryIndex.index.getClusterExpansionZoom(numericId);
}

export function flooredOverlayZoom(zoom) {
  const value = Math.floor(Number(zoom));
  return Number.isFinite(value) ? value : 0;
}

export function overlayReportToFeature(report, category = report?.category, index = 0) {
  assertOverlayCategory(category);
  validateMockOverlayReport(report, index);
  if (report.category !== category) {
    throw new TypeError(`Invalid overlay report at index ${index}: expected ${category} category.`);
  }
  const config = HEX_OVERLAY_CATEGORIES[category];
  const id = `${category}:${report.id}`;
  return Object.freeze({
    type: "Feature",
    id,
    geometry: Object.freeze({ type: "Point", coordinates: Object.freeze([report.longitude, report.latitude]) }),
    properties: Object.freeze({
      id: report.id,
      category,
      kind: config.pointKind,
      clusterCategory: category,
      clusterInternalId: id,
      subtype: report.subtype,
      severity: report.severity,
      reportDate: report.reportDate,
    }),
  });
}

function toOverlayClusterFeature(feature, categoryIndex) {
  const properties = feature.properties || {};
  const config = categoryIndex.config;
  const pointCount = Number(properties.point_count) || 0;
  return {
    type: "Feature",
    id: `${categoryIndex.category}:cluster:${properties.cluster_id}`,
    geometry: cloneGeometry(feature.geometry),
    properties: {
      category: categoryIndex.category,
      kind: config.clusterKind,
      clusterCategory: categoryIndex.category,
      clusterId: properties.cluster_id,
      pointCount,
      clusterLabel: String(pointCount),
      radius: overlayClusterRadius(pointCount),
    },
  };
}

function toOverlayPointFeature(feature, categoryIndex) {
  const config = categoryIndex.config;
  const properties = feature.properties || {};
  return {
    type: "Feature",
    id: feature.id ?? properties.clusterInternalId,
    geometry: cloneGeometry(feature.geometry),
    properties: {
      ...properties,
      category: categoryIndex.category,
      kind: config.pointKind,
      clusterCategory: categoryIndex.category,
    },
  };
}

function overlayClusterRadius(pointCount) {
  const stops = HEX_OVERLAY_STYLE.clusterRadiusStops;
  if (pointCount <= stops[0][0]) return stops[0][1];
  for (let index = 0; index < stops.length - 1; index += 1) {
    const [startCount, startRadius] = stops[index];
    const [endCount, endRadius] = stops[index + 1];
    if (pointCount <= endCount) return startRadius + ((pointCount - startCount) / (endCount - startCount)) * (endRadius - startRadius);
  }
  return stops.at(-1)[1];
}

function cloneGeometry(geometry) {
  if (!geometry || geometry.type !== "Point" || !Array.isArray(geometry.coordinates)) {
    throw new TypeError("Expected a Point geometry from Supercluster.");
  }
  return { type: "Point", coordinates: [...geometry.coordinates] };
}
